// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import { ReactNode } from 'react';
import TextArea from 'presentations/views/components/atoms/TextArea';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import {
  borderRadius,
  mediaMax,
  spacing,
  fontSize,
} from 'presentations/views/styles';
import { white, gray06, gray05 } from 'presentations/views/styles/color';
import EventTypeHeader from 'presentations/views/components/organisms/EventTypeHeader';
import Headline from 'presentations/views/components/atoms/Headline';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { SharedEventDetailItem } from 'presentations/api/schema/event';
import EventSummary from 'presentations/views/components/organisms/EventSummary';
import EventContact from 'presentations/views/components/organisms/EventContact';
import Button from 'presentations/views/components/atoms/Button';
import { useEventCancellationPage } from './hooks';
import { eventPath } from 'presentations/routes/path';
import { EventContactItem } from 'presentations/api/schema/event_contact';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { ViewerItem } from 'presentations/api/schema/user';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import { Status } from 'domains/models/event/status';
import { SharedEventTypeItem } from 'presentations/api/schema/event_type';
import Snackbar, { SeverityType } from '../../atoms/Snackbar';

interface Props {
  viewer: ViewerItem | null;
  breadcrumbs: ReactNode;
  eventContact: EventContactItem;
  email?: string;
  message?: string;
  event: SharedEventDetailItem;
  eventType: SharedEventTypeItem;
  host: EventTypeUserListItem;
  onChange?: any;
  teamImage: string | null;
  teamName: string | null;
  confirmText: string | null;
  isShowHeaderTeamImage: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useEventCancellationPage({
    viewer: props.viewer,
    event: props.event,
    eventType: props.eventType,
  });
  const headerTeamImage = props.isShowHeaderTeamImage
    ? props.event.team?.headerTeamImage
    : null;
  const isBookingRequest = props.event.status === Status.Request;
  const bookingRequestSuffix = isBookingRequest ? ' request' : '';

  const onClick = async (e: any) => {
    e.preventDefault();
    actions.setCancelEvent(props.event);
  };

  const onSubmit = async () => {
    await actions.onCancelEvent();
    actions.setCancelEvent(null);
  };

  const onCloseDialog = () => {
    actions.setCancelEvent(null);
  };

  return (
    <ServicePageTemplate
      header={
        <EventTypeHeader
          viewer={props.viewer}
          headerTeamImage={headerTeamImage}
        />
      }
      isHiddenContentFooter
      isHiddenFooter
      breadcrumbs={props.breadcrumbs}
    >
      <Container>
        <Box>
          <HeadlineContainer>
            <TitleHeadlineIcon color="primary" name="calendar-month" />
            <TitleHeadline variant="h1">
              {t(`pageTitle${bookingRequestSuffix}`)}
            </TitleHeadline>
          </HeadlineContainer>
          {state.isPast
            ? t(`description past`)
            : t(`description${bookingRequestSuffix}`)}
          <EventSummary event={props.event} />
          <Hr />
          <EventContact
            viewer={props.viewer}
            event={props.event}
            eventContact={props.eventContact}
            isShowNotice={false}
          />
          {!state.isPast && (
            <>
              {isBookingRequest ? null : (
                <>
                  <Hr />
                  <MessageTitleContainer>
                    <Message>{t('MessageTitle')}</Message>
                    <MessageDescription>
                      {t('MessageDescription')}
                    </MessageDescription>
                  </MessageTitleContainer>
                  <TextArea
                    isFullWidth
                    name="message"
                    placeholder={t('MessagePlaceholder')}
                    onChange={actions.onChange}
                    errors={state.errors['cancelReason']}
                  />
                </>
              )}
              <ButtonContainer>
                <Button
                  color="primary"
                  size="large"
                  href={eventPath(props.event.uid)}
                  onClick={onClick}
                  isDisabled={props.event.status === Status.Cancel}
                >
                  {props.event.status === Status.Cancel
                    ? t('Canceled')
                    : t(`Event Cancel${bookingRequestSuffix}`)}
                </Button>
              </ButtonContainer>
            </>
          )}
          {/* <PoweredByContainer>
            <PoweredByLink />
          </PoweredByContainer> */}
        </Box>
      </Container>
      <ConfirmationDialog
        isOpen={state.cancelEvent !== null}
        onSubmit={onSubmit}
        submitLabel={translate('Yes')}
        onCancel={onCloseDialog}
        cancelLabel={translate('No')}
        title={t(`Cancel?${bookingRequestSuffix}`)}
        isLoading={state.isLoading}
      />
      {!!state.errorMessage && (
        <Snackbar
          onClose={actions.onCloseErrorMessage}
          isOpen={Boolean(state.errorMessage)}
          severity={SeverityType.Error}
          message={state.errorMessage}
        />
      )}
    </ServicePageTemplate>
  );
}

const Container = styled.div`
  background-color: ${gray06};
  padding: ${spacing(48)} ${spacing(24)};
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(24)};
  }
  ${mediaMax.smartphone} {
    padding: ${spacing(24)} ${spacing(12)};
  }
`;
const Box = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: ${spacing(48)};
  background-color: ${white};
  border-radius: ${borderRadius(16)};
  ${mediaMax.tablet} {
    padding: ${spacing(24)};
  }
  ${mediaMax.smartphone} {
    padding: ${spacing(24)} ${spacing(20)};
  }
`;
const HostContainer = styled.div`
  margin-bottom: ${spacing(32)};
`;

const HeadlineContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(32)};
`;
const TitleHeadline = styled(Headline)``;
const TitleHeadlineIcon = styled(Icon)`
  margin-right: ${spacing(8)};
  font-size: ${fontSize(32)};
  width: 36px !important;
  height: 36px !important;
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;

export const Hr = styled.div`
  height: 1px;
  margin: ${spacing(32)} 0;
  background-color: ${gray05};
`;

const ButtonContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${mediaMax.smartphone} {
    text-align: center;
  }
`;
const EventDetailWithoutRegistration = styled(Link)`
  display: block;
  margin: ${spacing(16)} 0 0;
`;

const MessageTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${spacing(8)};
`;

const Message = styled.div`
  font-size: ${fontSize(14)};
  font-weight: bold;
`;

const MessageDescription = styled.div`
  font-size: ${fontSize(12)};
  font-weight: bold;
  margin: 0px ${spacing(6)};
  padding: 1px;
`;
